import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { DIAMOND_DUE_RATIO } from '@shared/lib/constants';
import { Money } from '@shared/lib/shopify/types';
import { rPercentage } from '@shared/lib/utils/formats';
import { multiplyMoney, renderMoney } from '@shared/lib/utils/money';
import clsx from 'clsx';
import Link from 'next/link';
import { IconType } from 'react-icons';
import { AiOutlinePieChart } from 'react-icons/ai';
import { FaShippingFast } from 'react-icons/fa';
import { LiaFileInvoiceSolid, LiaFlagUsaSolid } from 'react-icons/lia';
import { PiCircleHalfDuotone } from 'react-icons/pi';
import { RiRefund2Line } from 'react-icons/ri';

export default function Policy({
  name,
  description,
  Icon,
  className,
}: {
  name: string;
  description: string | React.ReactNode;
  Icon?: IconType;
  className?: string;
}) {
  return (
    <div
      key={name}
      className={clsx(
        'border-bd-contrast bg-bd-secondary rounded-lg border p-6 text-center',
        className
      )}
    >
      {Icon && (
        <dt>
          <Icon aria-hidden="true" className="text-bd-primary mx-auto h-6 w-6 shrink-0" />
          <span className="mt-4 text-sm font-medium">{name}</span>
        </dt>
      )}
      <dd className={clsx('text-bd-dark/80 mt-1 text-sm', className)}>{description}</dd>
    </div>
  );
}

export function MadeInTheUSA() {
  return (
    <Policy
      name="Made in the USA"
      description="We are a proud American company that crafts all of our jewelry in the USA."
      Icon={LiaFlagUsaSolid}
    />
  );
}

export function BondedClubRewards() {
  return (
    <Policy
      name="Bonded Club Discounts"
      description={
        <Link href="/club">
          You may save 5-15% when you join our loyalty program. Check out the{' '}
          <span className="underline">Bonded Diamond™ Club</span> for limited time offers.
        </Link>
      }
      Icon={CurrencyDollarIcon}
    />
  );
}

export function StartedKitRefundable() {
  return (
    <Policy
      name="Started Kit Refundable"
      description="The cost of the started kit is fully refundable if you choose to order any Bonded Diamond™."
      Icon={RiRefund2Line}
    />
  );
}

export function PayIn4EasyInstallments() {
  return (
    <Policy
      name="No Credit Check 4 Easy Installments"
      description="We offer a no credit check 4 easy payment plan to our Bonded Diamond™ and Bonded Jewelry. You will pay 25% of the total cost today, and the rest will be paid in 3 monthly installments."
      Icon={AiOutlinePieChart}
    />
  );
}

export function FreeShipping() {
  return (
    <Policy
      name="Free Shipping"
      description="Shipping and return shipping are free for all orders."
      Icon={FaShippingFast}
    />
  );
}
export function FreeShippingAndReturnShipping() {
  return (
    <Policy
      name="Free Shipping & Returns"
      description="Free Shipping and return shipping for the get started kit."
      Icon={FaShippingFast}
    />
  );
}

export function NoCommitment() {
  return (
    <Policy
      name="No Commitment"
      description="You will only pay $50 for the refundable started kit to kick start your order. There is no commitment to make a purchase eventually."
      Icon={LiaFileInvoiceSolid}
    />
  );
}

export function PayIn4Plan({ totalAmount }: { totalAmount: Money }) {
  return (
    <Policy
      name="No Credit Check 4 Easy Installments"
      description={`If you elect to our no credit check 4 easy payment plan. You will pay 25% (${renderMoney(multiplyMoney(totalAmount, 0.25))}) + tax at order confirmation, and the rest will be paid in 3 monthly installments. Terms and conditions apply.`}
      Icon={AiOutlinePieChart}
    />
  );
}

export function PayIn2Plan({ totalAmount }: { totalAmount: Money }) {
  return (
    <Policy
      name={`${rPercentage(DIAMOND_DUE_RATIO)} Upfront`}
      description={`If you elect to our ${rPercentage(DIAMOND_DUE_RATIO)} upfront payment plan. You will pay ${rPercentage(DIAMOND_DUE_RATIO)} (${renderMoney(multiplyMoney(totalAmount, DIAMOND_DUE_RATIO))}) + tax at order confirmation, and the remaining will be collected before the time of order delivery.`}
      Icon={PiCircleHalfDuotone}
    />
  );
}

export function PayIn4PlanRaw() {
  return (
    <Policy
      name="No Credit Check 4 Easy Installments"
      description={`Pay 25% + tax today as deposit, and the rest will be paid in 3 monthly installments. Terms and conditions apply.`}
      Icon={AiOutlinePieChart}
    />
  );
}

export function PayIn2PlanRaw() {
  return (
    <Policy
      name={`${rPercentage(DIAMOND_DUE_RATIO)} Upfront`}
      description={`Pay ${rPercentage(DIAMOND_DUE_RATIO)} + tax today as deposit, and the rest will be paid before the time of order delivery.`}
      Icon={PiCircleHalfDuotone}
    />
  );
}
